import { useAuth } from "../contexts/authContext";
import { getUserQuotes, createNewQuote } from "../backend/db";
import { useEffect, useState } from "react";
import QuoteCard from "./QuoteCard";
import { onSnapshot } from "firebase/firestore";
import DownloadIcon from "./DownloadIcon";
import downloadBg from "../assets/download_bg.png";
import { franc } from "franc-min";

const Profile = () => {
  const { currentUser } = useAuth();

  const [quotes, setQuotes] = useState([]);
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [canvasAuthor, setCanvasAuthor] = useState("");
  const [canvasContent, setCanvasContent] = useState("");
  const [displayCanvas, setDisplayCanvas] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      getUserQuotes(currentUser.uid),
      (snapshot) => {
        const quotesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setQuotes(quotesData);
      }
    );
    return unsubscribe;
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await createNewQuote(currentUser.uid, author, content);
      setIsCreating(false);
      setAuthor("");
      setContent("");
      setShowForm(false);
    } catch (error) {
      console.log(error);
      setIsCreating(false);
    }
  };

  const handleCreateButtonClick = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
  };

  useEffect(() => {
    if (displayCanvas) {
      const canvasContainer = document.getElementById("card-canvas");
      console.log("canvasContainer width: ", canvasContainer.offsetWidth);
      const canvas = document.createElement("canvas");
      canvasContainer.innerHTML = ""; // Clear previous canvas
      canvasContainer.appendChild(canvas);

      let deviceWidth = window.innerWidth;
      console.log("deviceWidth: ", deviceWidth);

      let previewRatio = canvasContainer.offsetWidth / 1080;

      draw(previewRatio, canvas);
    }
  }, [displayCanvas, canvasAuthor, canvasContent]);

  const onCardClick = async (author, content) => {
    setCanvasAuthor(author);
    setCanvasContent(content);
    setDisplayCanvas(true);
  };

  const draw = (previewRatio, canvas) => {
    return new Promise((resolve, reject) => {
      canvas.width = 1080 * previewRatio;
      canvas.height = 1080 * previewRatio;
      const padddingTop = 413 * previewRatio;
      const lineHeight = 30 * previewRatio;
      const contentWidth = 763 * previewRatio;
      const contentFontSize = 30 * previewRatio;
      const authorFontSize = 26 * previewRatio;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw background
      const backgroundImage = document.createElement("img");
      backgroundImage.src = downloadBg;
      backgroundImage.onload = () => {
        console.log("draw");
        ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
        // Draw content
        ctx.fillStyle = "#2A418F";
        ctx.font = `normal ${contentFontSize}px PT Mono`;
        ctx.textAlign = "center";

        // Language detection and wrapping logic
        const language = franc(canvasContent);
        const isCJK = ["cmn", "jpn", "kor"].includes(language);
        let line = "";
        let y = padddingTop + lineHeight;
        if (isCJK) {
          // Handle CJK text wrapping
          for (let i = 0; i < canvasContent.length; i++) {
            const testLine = line + canvasContent[i];
            const metrics = ctx.measureText(testLine);
            const testWidth = metrics.width;

            if (testWidth > contentWidth && line !== "") {
              ctx.fillText(line, canvas.width / 2, y);
              y += lineHeight + 3;
              line = canvasContent[i];
            } else {
              line = testLine;
            }
          }
        } else {
          const words = canvasContent.split(" ");
          words.forEach((word) => {
            const testLine = line + word + " ";
            const metrics = ctx.measureText(testLine);
            const testWidth = metrics.width;
            if (testWidth > contentWidth && line !== "") {
              console.log("draw line");
              ctx.fillText(line, canvas.width / 2, y);
              y += lineHeight + 3;
              line = word + " ";
            } else {
              line = testLine;
            }
          });
        }

        ctx.fillText(line, canvas.width / 2, y);
        y += lineHeight;

        // Draw author
        ctx.font = `normal ${authorFontSize}px PT Mono`;
        ctx.fillText(
          `- ${canvasAuthor}`,
          canvas.width / 2,
          y + 117 * previewRatio
        ); // Adjust position as needed
        resolve(canvas); // Rsolve the promise
      };
      backgroundImage.onerror = (error) => {
        console.error("Failed to load the background image", error);
        reject(error); // Reject the promise if there's an error
      };
    });
  };

  const onDownload = async () => {
    console.log("click download");
    // Create download link
    const canvas = await draw(1, document.createElement("canvas"));
    const link = document.createElement("a");
    link.download = "quote.png";
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <>
      <main
        className="w-full min-h-screen flex flex-col self-center  place-items-center mb-5
      bg-main_dark text-sub p-5"
      >
        <div className="w-full">
          <h1 className="mb-3">My Quotes Page</h1>
          <p className="mb-4">{currentUser.email}</p>
          <button
            onClick={handleCreateButtonClick}
            className="w-1/2 lg:w-5/12 self-end px-4 py-2 mb-5 font-medium rounded-lg border-solid border-2 border-sub bg-deco  hover:shadow-xl transition duration-300"
          >
            Create new quote
          </button>
        </div>

        {quotes.length === 0 ? (
          <div>No quote yet! Create a new quote!</div>
        ) : (
          quotes.map((quote) => (
            <QuoteCard
              key={quote.id}
              id={quote.id}
              author={quote.author}
              content={quote.content}
              userId={currentUser.uid}
              onCardClick={() => onCardClick(quote.author, quote.content)}
            />
          ))
        )}

        {/* ---------- create new quote form ----------*/}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="relative bg-card p-6 rounded-lg shadow-md w-96 ">
              <button
                type="button"
                onClick={handleCloseForm}
                className="absolute top-2 right-2 text-red-600 text-2xl"
              >
                &times;
              </button>
              <form onSubmit={onSubmit}>
                <h2 className="text-xl mb-4">Create new quote</h2>
                <div>
                  <label className="text-sm  font-bold">Quote content</label>
                  <textarea
                    type="text"
                    required
                    value={content}
                    onChange={(e) => {
                      setContent(e.target.value);
                    }}
                    className="w-full mt-2 px-3 py-2  bg-transparent 
                  outline-none border border-gray-400 focus:border-sub shadow-sm rounded-lg transition duration-300"
                  ></textarea>
                </div>
                <div className="mt-3">
                  <label className="text-sm font-bold">Quote author</label>
                  <input
                    type="text"
                    required
                    value={author}
                    onChange={(e) => {
                      setAuthor(e.target.value);
                    }}
                    className="w-full mt-2 px-3 py-2  bg-transparent outline-none border border-gray-400 focus:border-sub shadow-sm rounded-lg transition duration-300"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isCreating}
                  className={`w-full mt-4 px-4 py-2 font-medium rounded-lg border-solid border-2 border-sub ${
                    isCreating
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-deco hover:shadow-xl transition duration-300"
                  }`}
                >
                  {isCreating ? "Creating ..." : "Create"}
                </button>
              </form>
            </div>
          </div>
        )}
        {/* ---------- quote canvas ----------*/}
        {displayCanvas && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="relative bg-card p-6 rounded-lg shadow-md w-4/5 lg:w-5/12">
              <button
                type="button"
                onClick={() => setDisplayCanvas(false)}
                className="absolute top-0 right-2 text-red-600 text-2xl"
              >
                &times;
              </button>
              <div id="card-canvas" className="w-full mb-3"></div>

              <div className="flex items-end justify-end">
                <button onClick={onDownload}>
                  <DownloadIcon />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default Profile;
